import type { ProductCardProduct } from 'components/product/ProductCard';

const asStringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

/**
 * Resolves a storefront path slug from a reference object.
 * Checks `ref.slug` first, then `ref.route.slug`; ensures a leading `/`.
 */
const readSlug = (ref: Record<string, unknown>): string => {
    const topLevelSlug = ref.slug;
    if (typeof topLevelSlug === 'string' && topLevelSlug.length > 0) {
        return topLevelSlug.startsWith('/') ? topLevelSlug : `/${topLevelSlug}`;
    }
    const { route } = ref;
    if (route && typeof route === 'object' && route !== null && !Array.isArray(route)) {
        const { slug } = route as { slug?: string };
        if (typeof slug === 'string' && slug.length > 0) {
            return slug.startsWith('/') ? slug : `/${slug}`;
        }
    }
    return '';
};

/**
 * Reads designer display name from `product.designer.name` when present and valid.
 */
const readDesignerName = (product: Record<string, unknown>): string | undefined => {
    const { designer } = product;
    if (!designer || typeof designer !== 'object' || designer === null || Array.isArray(designer)) {
        return undefined;
    }
    const { name } = designer as { name?: string };
    return typeof name === 'string' && name.length > 0 ? name : undefined;
};

/**
 * Maps a page `references[productId]` entry (PIM-style payload from resolve-app) to {@link ProductCardProduct}.
 *
 * Expected shape: `{ externalId?, slug?, route?, product?: { fullName, name, thumbnail, … } }`.
 * Returns `null` if the reference is not an object, has no nested `product`, or no resolvable slug.
 *
 * `lookupKey` is used as a fallback when `externalId` is missing (e.g. the map key from `references`).
 * Numeric fields such as `price` and `stock` are placeholders here; cards may enrich from elsewhere.
 *
 * @param ref - Raw reference value from the page payload.
 * @param lookupKey - Fallback id when `externalId` is absent or empty.
 */
export const mapPageReferenceToProductCard = (ref: unknown, lookupKey: string): ProductCardProduct | null => {
    if (!ref || typeof ref !== 'object' || Array.isArray(ref)) {
        return null;
    }

    const refRecord = ref as Record<string, unknown>;
    const { product } = refRecord;
    if (!product || typeof product !== 'object' || Array.isArray(product)) {
        return null;
    }

    const productRecord = product as Record<string, unknown>;
    const slug = readSlug(refRecord);
    if (!slug) {
        return null;
    }

    const externalIdRaw = refRecord.externalId;
    const externalId =
        typeof externalIdRaw === 'string' && externalIdRaw.trim().length > 0 ? externalIdRaw.trim() : lookupKey.trim();
    if (!externalId) {
        return null;
    }

    const productIdNumeric = Number.parseInt(externalId, 10);
    const productId = Number.isFinite(productIdNumeric) ? productIdNumeric : 0;

    const fullName = typeof productRecord.fullName === 'string' ? productRecord.fullName : '';
    const nameShort = typeof productRecord.name === 'string' ? productRecord.name : '';
    const name = fullName || nameShort || externalId;

    const thumbnail =
        (typeof productRecord.thumbnail === 'string' && productRecord.thumbnail) ||
        (asStringArray(productRecord.photosOnWhite)[0] ?? '') ||
        '';

    const imagery = asStringArray(productRecord.imagery);
    const categories = asStringArray(productRecord.categories);

    const familyField = typeof productRecord.family === 'string' ? productRecord.family : '';
    const { productFamily } = productRecord;
    const familyFromNested =
        productFamily && typeof productFamily === 'object' && productFamily !== null && !Array.isArray(productFamily)
            ? String((productFamily as { name?: string }).name ?? '')
            : '';
    const family = familyField || familyFromNested;

    const packShotSize =
        typeof productRecord.packShotSize === 'string' && productRecord.packShotSize.length > 0
            ? productRecord.packShotSize
            : undefined;

    const badgeRaw = productRecord.badge;
    const badge = typeof badgeRaw === 'string' && badgeRaw.length > 0 ? badgeRaw : undefined;

    const designer = readDesignerName(productRecord);

    return {
        categories,
        family,
        image: thumbnail,
        imageKey: '',
        imagery: imagery.length > 0 ? imagery : thumbnail ? [thumbnail] : [],
        manufacturer: { name: '' },
        name,
        objectID: externalId,
        price: 0,
        pricing: {},
        productId,
        slug,
        stock: 0,
        thumbnail,
        uniqueName: fullName || nameShort || externalId,
        url: slug,
        packShotSize,
        badge,
        designer,
    };
};
